import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ShoppingBag,
  Sparkles,
  MapPin,
  Quote,
  Link2,
  Plus,
  Check,
  X,
  CircleAlert,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import {
  useContentPackages,
  useContentOrders,
  useRequestOrder,
  useCancelOrder,
  type ContentPackage,
  type ContentOrder,
  type ContentKind,
  type OrderState,
  type OrderStage,
  type OrderMetadata,
} from '@/lib/content';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/growth')({
  staticData: { crumb: 'Wzrost' },
  component: GrowthPage,
});

const KIND_LABEL: Record<ContentKind, string> = {
  blog: 'Blog',
  local_seo_city: 'Lokalne SEO',
  expert_quote: 'Cytaty eksperckie',
  backlink_outreach: 'Outreach + linki',
};

const KIND_ICON: Record<ContentKind, React.ComponentType<{ className?: string }>> = {
  blog: Sparkles,
  local_seo_city: MapPin,
  expert_quote: Quote,
  backlink_outreach: Link2,
};

const STATE_LABEL: Record<OrderState, string> = {
  requested: 'Zlecone — czekamy na operatora',
  active: 'Aktywne',
  paused: 'Wstrzymane',
  cancelled: 'Anulowane',
  completed: 'Zakończone',
};

const STAGE_LABEL: Record<OrderStage, string> = {
  pending: 'Czeka',
  in_progress: 'W realizacji',
  partial: 'Częściowo dostarczone',
  delivered: 'Dostarczone',
  indexed: 'Zaindeksowane',
  failed: 'Niepowodzenie',
};

const STAGE_ORDER: OrderStage[] = ['pending', 'in_progress', 'delivered', 'indexed'];

function GrowthPage() {
  const { session } = Route.useRouteContext();
  if (session.role === 'operator') return null;

  const packages = useContentPackages();
  const orders = useContentOrders();
  const requestOrder = useRequestOrder();
  const cancelOrder = useCancelOrder();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [notes, setNotes] = React.useState('');
  const [landingPageUrl, setLandingPageUrl] = React.useState('');
  const [anchorTextsRaw, setAnchorTextsRaw] = React.useState('');
  const [feedback, setFeedback] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );

  const ordersById = new Map((orders.data ?? []).map((o) => [o.package_id, o]));
  const selected = packages.data?.find((p) => p.id === selectedId) ?? null;

  const onSubscribe = async (pkgId: string) => {
    setFeedback(null);
    const pkg = packages.data?.find((p) => p.id === pkgId);
    const metadata: OrderMetadata | undefined =
      pkg?.kind === 'backlink_outreach'
        ? {
            landing_page_url: landingPageUrl || undefined,
            anchor_texts: anchorTextsRaw
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean),
          }
        : undefined;
    try {
      await requestOrder.mutateAsync({
        package_id: pkgId,
        notes: notes || undefined,
        metadata,
      });
      setNotes('');
      setLandingPageUrl('');
      setAnchorTextsRaw('');
      setSelectedId(null);
      setFeedback({
        kind: 'ok',
        text: 'Zlecenie wysłane — nasz operator skontaktuje się w ciągu 24h.',
      });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    }
  };

  const onCancel = async (id: string) => {
    setFeedback(null);
    try {
      await cancelOrder.mutateAsync(id);
      setFeedback({ kind: 'ok', text: 'Subskrypcja anulowana.' });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Wzrost — content marketplace
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Zamów cykliczny content i pozycjonowanie u zespołu pozyskajpacjenta.pl.
          Pakiety są comiesięczne — można je w każdej chwili wstrzymać lub anulować.
        </p>
      </header>

      {feedback ? (
        <p
          role="status"
          className={cn(
            'rounded-md px-4 py-2 text-sm ring-1',
            feedback.kind === 'ok'
              ? 'bg-success/10 text-success ring-success/20'
              : 'bg-error/10 text-error ring-error/20'
          )}
        >
          {feedback.text}
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingBag className="size-5" aria-hidden="true" /> Katalog pakietów
          </CardTitle>
          <CardDescription>
            Wybierz pakiet i kliknij Zamów — operator skonfiguruje rozliczenie
            oraz przydzieli zespół copywriterów / specjalistów SEO.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {packages.isPending ? (
            <p className="text-sm text-text-muted">Ładowanie katalogu…</p>
          ) : (
            <ul role="list" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {(packages.data ?? []).map((p) => {
                const order = ordersById.get(p.id);
                const Icon = KIND_ICON[p.kind] ?? Sparkles;
                const active =
                  order &&
                  (order.state === 'requested' || order.state === 'active' || order.state === 'paused');
                return (
                  <li
                    key={p.id}
                    className={cn(
                      'flex flex-col gap-3 rounded-[var(--radius-card)] bg-surface p-4 ring-1 ring-black/5',
                      selectedId === p.id && 'ring-2 ring-accent/40'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <span className="grid size-9 place-items-center rounded-md bg-accent/10 text-accent ring-1 ring-accent/20">
                        <Icon className="size-4" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-base font-semibold text-text">{p.name}</p>
                        <p className="mt-0.5 text-xs uppercase tracking-wide text-text-subtle">
                          {KIND_LABEL[p.kind]} · {p.cadence_per_month}/mies. · Plan{' '}
                          {p.requires_tier}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-text-muted">{p.tagline}</p>
                    <p className="font-display text-2xl font-semibold tabular-nums text-text">
                      {p.price_label}
                    </p>
                    <div className="mt-auto flex gap-2">
                      <Button
                        type="button"
                        variant={selectedId === p.id ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
                      >
                        Szczegóły
                      </Button>
                      {active ? (
                        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-1 text-xs text-success ring-1 ring-success/20">
                          <Check className="size-3" aria-hidden="true" />
                          {STATE_LABEL[order!.state]}
                        </span>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          className="ml-auto"
                          onClick={() => {
                            setSelectedId(p.id);
                            setTimeout(() => {
                              const el = document.getElementById(`order-form-${p.id}`);
                              el?.scrollIntoView({ behavior: 'smooth' });
                            }, 0);
                          }}
                        >
                          <Plus className="size-3.5" aria-hidden="true" /> Zamów
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {selected ? (
        <PackageDetail
          pkg={selected}
          existing={ordersById.get(selected.id) ?? null}
          notes={notes}
          onNotesChange={setNotes}
          landingPageUrl={landingPageUrl}
          onLandingPageUrlChange={setLandingPageUrl}
          anchorTextsRaw={anchorTextsRaw}
          onAnchorTextsChange={setAnchorTextsRaw}
          onSubscribe={() => onSubscribe(selected.id)}
          onClose={() => setSelectedId(null)}
          busy={requestOrder.isPending}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Moje zamówienia</CardTitle>
          <CardDescription>
            Status aktywnych i historycznych subskrypcji contentowych. Operator dostarcza
            content zgodnie z harmonogramem (4× lub 6× w miesiącu w zależności od pakietu).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {orders.isPending ? (
            <p className="text-sm text-text-muted">Ładowanie…</p>
          ) : (orders.data ?? []).length === 0 ? (
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak zamówień. Wybierz pakiet z katalogu powyżej.
            </p>
          ) : (
            <ul role="list" className="space-y-2">
              {(orders.data ?? []).map((o) => (
                <OrderRow key={o.id} order={o} onCancel={() => onCancel(o.id)} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PackageDetail({
  pkg,
  existing,
  notes,
  onNotesChange,
  landingPageUrl,
  onLandingPageUrlChange,
  anchorTextsRaw,
  onAnchorTextsChange,
  onSubscribe,
  onClose,
  busy,
}: {
  pkg: ContentPackage;
  existing: ContentOrder | null;
  notes: string;
  onNotesChange: (v: string) => void;
  landingPageUrl: string;
  onLandingPageUrlChange: (v: string) => void;
  anchorTextsRaw: string;
  onAnchorTextsChange: (v: string) => void;
  onSubscribe: () => void;
  onClose: () => void;
  busy: boolean;
}) {
  const isActive =
    existing &&
    (existing.state === 'requested' ||
      existing.state === 'active' ||
      existing.state === 'paused');
  const isBacklink = pkg.kind === 'backlink_outreach';
  return (
    <Card id={`order-form-${pkg.id}`}>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>{pkg.name}</CardTitle>
          <CardDescription>{pkg.description}</CardDescription>
        </div>
        <Button type="button" variant="ghost" size="icon" aria-label="Zamknij" onClick={onClose}>
          <X className="size-4" aria-hidden="true" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
              Zakres pakietu
            </p>
            <ul role="list" className="mt-2 space-y-1 text-sm">
              {pkg.scope.map((s) => (
                <li key={s} className="flex items-start gap-2">
                  <Check className="mt-0.5 size-4 text-success" aria-hidden="true" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
              Przykładowa publikacja
            </p>
            <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-surface p-3 text-xs leading-relaxed text-text ring-1 ring-black/5">
              {pkg.sample_md}
            </pre>
          </div>
        </div>

        {isActive ? (
          <p className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
            Masz już aktywne zamówienie tego pakietu (status: {STATE_LABEL[existing!.state]}).
            Aby zamówić ponownie, najpierw anuluj bieżące zamówienie w sekcji „Moje zamówienia".
          </p>
        ) : (
          <div className="space-y-3 rounded-md bg-surface p-4 ring-1 ring-black/5">
            {isBacklink ? (
              <>
                <Field
                  label="Docelowa strona (landing page)"
                  hint="Strona na Twojej witrynie, na którą mają wskazywać linki."
                  htmlFor="landing"
                  required
                >
                  <Input
                    id="landing"
                    type="url"
                    placeholder="https://klinikaabc.pl/zabiegi/sterylizacja-suczki"
                    value={landingPageUrl}
                    onChange={(e) => onLandingPageUrlChange(e.target.value)}
                  />
                </Field>
                <Field
                  label="Preferowane anchor-texty"
                  hint="Jeden na linię. Mix brandowy / generyczny / topical — zostawimy decyzje końcowe naszemu zespołowi."
                  htmlFor="anchors"
                >
                  <Textarea
                    id="anchors"
                    rows={4}
                    placeholder={
                      'klinika weterynaryjna ABC w Warszawie\nweterynarz Warszawa Mokotów\nsprawdź ofertę kliniki'
                    }
                    value={anchorTextsRaw}
                    onChange={(e) => onAnchorTextsChange(e.target.value)}
                  />
                </Field>
              </>
            ) : null}
            <Field
              label="Uwagi do zamówienia (opcjonalnie)"
              hint="Np. preferowana tematyka, słowa kluczowe z Search Console, deadliny."
              htmlFor="notes"
            >
              <Textarea
                id="notes"
                rows={3}
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
              />
            </Field>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-text-muted">
                Cena: <span className="font-display font-semibold text-text">{pkg.price_label}</span>
                {' — '}rozliczenie miesięczne, anulujesz w każdej chwili.
              </p>
              <Button
                type="button"
                onClick={onSubscribe}
                disabled={busy || (isBacklink && !landingPageUrl)}
              >
                <ShoppingBag className="size-4" aria-hidden="true" />
                Zamów pakiet
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OrderRow({ order, onCancel }: { order: ContentOrder; onCancel: () => void }) {
  const Icon = order.package_kind ? KIND_ICON[order.package_kind] : Sparkles;
  const canCancel = order.state === 'requested' || order.state === 'active' || order.state === 'paused';
  const isBacklink = order.package_kind === 'backlink_outreach';
  const landing =
    typeof order.metadata?.landing_page_url === 'string'
      ? order.metadata.landing_page_url
      : null;
  return (
    <li className="rounded-md bg-surface p-4 ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="grid size-9 place-items-center rounded-md bg-accent/10 text-accent ring-1 ring-accent/20">
            <Icon className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-semibold text-text">{order.package_name}</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Zlecone {new Date(order.requested_at * 1000).toLocaleString('pl-PL')}
              {order.price_label ? ` · ${order.price_label}` : ''}
            </p>
            {landing ? (
              <p className="mt-1 break-all text-xs text-text-muted">
                Landing: <span className="font-mono">{landing}</span>
              </p>
            ) : null}
            {order.notes ? (
              <p className="mt-1 text-xs italic text-text-subtle line-clamp-1">{order.notes}</p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <OrderStateBadge state={order.state} />
          {canCancel ? (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              <X className="size-3.5" aria-hidden="true" />
              Anuluj
            </Button>
          ) : null}
        </div>
      </div>

      {isBacklink ? (
        <>
          <StageTimeline current={order.stage} />
          {order.delivered_urls.length > 0 ? (
            <ul role="list" className="mt-3 space-y-1 border-t border-border pt-3">
              {order.delivered_urls.map((d, i) => (
                <li
                  key={`${d.url}-${i}`}
                  className="flex flex-wrap items-baseline gap-2 text-xs"
                >
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all text-accent hover:underline"
                  >
                    {d.url}
                  </a>
                  {d.dr != null ? (
                    <span className="rounded bg-bg px-1.5 py-0.5 font-mono text-text-subtle ring-1 ring-border">
                      DR {d.dr}
                    </span>
                  ) : null}
                  {d.indexed_at ? (
                    <span className="text-success">
                      ✓ zaindeksowany {new Date(d.indexed_at * 1000).toLocaleDateString('pl-PL')}
                    </span>
                  ) : null}
                  {d.anchor ? (
                    <span className="italic text-text-subtle">„{d.anchor}"</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </li>
  );
}

function StageTimeline({ current }: { current: OrderStage }) {
  if (current === 'failed') {
    return (
      <div className="mt-3 rounded-md bg-error/10 p-2 text-xs text-error ring-1 ring-error/20">
        {STAGE_LABEL.failed} — skontaktuj się z nami, aby uzgodnić zastępcze linki.
      </div>
    );
  }
  if (current === 'partial') {
    return (
      <div className="mt-3 rounded-md bg-warning/10 p-2 text-xs text-warning ring-1 ring-warning/20">
        Część linków już dostarczona; nad pozostałymi pracujemy.
      </div>
    );
  }
  const idx = STAGE_ORDER.indexOf(current);
  return (
    <ol role="list" className="mt-3 grid grid-cols-4 gap-2">
      {STAGE_ORDER.map((s, i) => {
        const reached = i <= idx;
        return (
          <li
            key={s}
            className={cn(
              'rounded-md px-2 py-1.5 text-center text-xs ring-1',
              reached
                ? 'bg-accent/10 text-accent ring-accent/20'
                : 'bg-bg text-text-subtle ring-border'
            )}
          >
            <span className="block text-[10px] font-mono uppercase">
              {String(i + 1).padStart(2, '0')}
            </span>
            {STAGE_LABEL[s]}
          </li>
        );
      })}
    </ol>
  );
}

function OrderStateBadge({ state }: { state: OrderState }) {
  const cls: Record<OrderState, string> = {
    requested: 'bg-warning/10 text-warning ring-warning/20',
    active: 'bg-success/10 text-success ring-success/20',
    paused: 'bg-surface text-text-muted ring-border',
    cancelled: 'bg-surface text-text-subtle ring-border',
    completed: 'bg-surface text-text-subtle ring-border',
  };
  const Icon = state === 'requested' ? CircleAlert : Check;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1', cls[state])}>
      <Icon className="size-3" aria-hidden="true" />
      {STATE_LABEL[state]}
    </span>
  );
}

// Silence unused-import warning when Link helper is needed later.
void Link;
